import {
  Bell,
  Bot,
  Building2,
  ChevronRight,
  ClipboardCheck,
  Clock,
  DollarSign,
  KeyRound,
  Lock,
  ShieldUser,
  User,
  Users,
  X,
  type LucideIcon
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Kbd } from "@/components/ui/kbd";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { ListSearch } from "@/components/ui/list-toolbar";
import { NavRow } from "@/components/ui/nav-row";
import { Switch } from "@/components/ui/switch";
import { LawFirmsAPI } from "../../api/lawFirms";
import type { LawFirm, SsoSetupStateDTO } from "../../api/types/lawFirm";
import { useGetUserDetails } from "../../hooks/useGetUserDetails";
import { cn } from "../../lib/utils";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { NotificationPreferences } from "../../features/notifications/NotificationPreferences";
import { TimeBillingSettings } from "../../features/timeTracking/settings/TimeBillingSettings";
import { TimeCaptureSettings } from "../../features/timeTracking/settings/TimeCaptureSettings";
import { AccountTab } from "./AccountTab";
import { AgentsTab } from "./AgentsTab";
import { CasePreparationTab } from "./CasePreparationTab";
import { useSettingsFirm } from "./useSettingsFirm";
import { GeneralTab } from "./GeneralTab";
import { PermissionsTab } from "./PermissionsTab";
import { SecurityTab } from "./SecurityTab";
import { TeamTab } from "./TeamTab";

type SettingsSection =
  | "profile"
  | "security"
  | "notifications"
  | "agents"
  | "general"
  | "case-preparation"
  | "team"
  | "permissions"
  | "sso"
  | "time-capture"
  | "time-billing";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSection?: SettingsSection | string;
};

/** The part after "#" in "case-preparation#firm-instructions": where to scroll. */
const anchorOf = (s: Props["initialSection"]): string | null =>
  s?.split("#")[1] || null;

const normalizeInitialSection = (
  value: Props["initialSection"]
): SettingsSection => {
  const s = value?.split("#")[0];
  if (
    s === "security" ||
    s === "notifications" ||
    s === "agents" ||
    s === "sso" ||
    s === "team" ||
    s === "permissions" ||
    s === "general" ||
    s === "case-preparation" ||
    s === "profile" ||
    s === "time-capture" ||
    s === "time-billing"
  ) {
    return s;
  }
  if (s === "account") return "profile";
  return "profile";
};

type NavEntry = {
  id: SettingsSection;
  label: string;
  icon: LucideIcon;
  group: "Account" | "Organization";
  visible: boolean;
};

const TITLES: Record<SettingsSection, string> = {
  profile: "Profile",
  security: "Authentication",
  notifications: "Notifications",
  agents: "Agents",
  "time-capture": "Time Capture",
  general: "General",
  "case-preparation": "Case Preparation",
  team: "Team",
  "time-billing": "Time & Billing",
  permissions: "Permissions",
  sso: "SSO"
};

const isLawFirmAdminRole = (role?: string | null): boolean => {
  const r = String(role || "")
    .trim()
    .toLowerCase();
  return r === "owner" || r === "admin" || r === "superadmin";
};

export function SettingsModal({ open, onOpenChange, initialSection }: Props) {
  const navigate = useNavigate();
  const {
    userDetails: { userType, role }
  } = useGetUserDetails();
  const lawyer = useSelector(lawyerSelectors.selectAll);
  const lawFirmId =
    (lawyer as { law_firm_id?: string | null })?.law_firm_id || null;

  const canManageSso = useMemo(() => {
    return userType === "Lawyer" && isLawFirmAdminRole(role);
  }, [userType, role]);
  const canManageTeam = useMemo(() => {
    return userType === "Lawyer" && isLawFirmAdminRole(role);
  }, [userType, role]);
  const canManageGeneral = useMemo(() => {
    return userType === "Lawyer" && isLawFirmAdminRole(role);
  }, [userType, role]);
  /** Members see Case Preparation read-only; admins change it. */
  const canSeeCasePreparation = userType === "Lawyer" && Boolean(lawFirmId);
  const canManagePermissions = useMemo(() => {
    return userType === "Lawyer" && isLawFirmAdminRole(role);
  }, [userType, role]);
  const canManageTimeBilling =
    userType === "Lawyer" && isLawFirmAdminRole(role);
  const timeTrackingEnabled = useSelector(
    lawyerSelectors.selectTimeTrackingEnabled
  );
  const canCaptureTime = userType === "Lawyer" && timeTrackingEnabled;
  const [section, setSection] = useState<SettingsSection>(
    normalizeInitialSection(initialSection)
  );
  const [anchor, setAnchor] = useState(() => anchorOf(initialSection));
  const openSection = (s: string) => {
    setSection(normalizeInitialSection(s));
    setAnchor(anchorOf(s));
  };
  const [query, setQuery] = useState("");
  const visibleSection = useMemo<SettingsSection>(() => {
    if (section === "time-capture" && !canCaptureTime) return "profile";
    if (section === "time-billing" && !canManageTimeBilling) return "profile";
    if (section === "general" && !canManageGeneral) return "profile";
    if (section === "case-preparation" && !canSeeCasePreparation)
      return "profile";
    if (section === "team" && !canManageTeam) return "profile";
    if (section === "permissions" && !canManagePermissions) return "profile";
    if (section === "sso" && !canManageSso) return "profile";
    return section;
  }, [
    section,
    canCaptureTime,
    canManageTimeBilling,
    canManageGeneral,
    canSeeCasePreparation,
    canManageSso,
    canManageTeam,
    canManagePermissions
  ]);
  const firmState = useSettingsFirm(
    lawFirmId,
    open && (canManageTeam || canSeeCasePreparation)
  );
  const settingsFirm = firmState.firm;

  const [ssoState, setSsoState] = useState<SsoSetupStateDTO | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [manageableTeamFirms, setManageableTeamFirms] = useState<LawFirm[]>([]);

  const configuredProviderLabel = useMemo(() => {
    const provider = (ssoState?.providers ?? [])[0];
    if (!provider) return null;
    return String(provider.label || provider.provider_id || "").trim() || null;
  }, [ssoState?.providers]);

  const close = () => onOpenChange(false);

  const handleOpenSsoSetup = () => {
    close();
    navigate("/law-firm/sso-setup");
  };

  const handleOpenSsoManagement = () => {
    close();
    navigate("/law-firm/sso-management");
  };

  const handleOpenSso = () => {
    // Mirror MFA row behavior: clicking/toggling opens the management flow.
    // We choose the most appropriate destination based on current state.
    if (!ssoState?.tenantId || !ssoState?.ssoEnabled) {
      handleOpenSsoSetup();
      return;
    }
    handleOpenSsoManagement();
  };

  useEffect(() => {
    let cancelled = false;
    const shouldFetch =
      open && section === "sso" && canManageSso && !!lawFirmId;

    if (shouldFetch) {
      const run = async () => {
        try {
          setSsoLoading(true);
          const payload = await LawFirmsAPI.getSsoSetup(lawFirmId!);
          if (!cancelled) setSsoState(payload);
        } catch {
          if (!cancelled) setSsoState(null);
        } finally {
          if (!cancelled) setSsoLoading(false);
        }
      };
      run();
    }

    return () => {
      cancelled = true;
    };
  }, [open, section, canManageSso, lawFirmId]);

  useEffect(() => {
    let cancelled = false;
    const shouldFetch =
      open && canManageTeam && !!lawFirmId && settingsFirm !== null;

    if (!shouldFetch) {
      setManageableTeamFirms([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        const descendants = await LawFirmsAPI.listDescendants(lawFirmId!);
        if (cancelled) return;
        setManageableTeamFirms([settingsFirm, ...descendants]);
      } catch {
        if (!cancelled) setManageableTeamFirms([]);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [open, canManageTeam, lawFirmId, settingsFirm]);

  const nav: NavEntry[] = [
    {
      id: "profile",
      label: TITLES.profile,
      icon: User,
      group: "Account",
      visible: true
    },
    {
      id: "security",
      label: TITLES.security,
      icon: KeyRound,
      group: "Account",
      visible: true
    },
    {
      id: "notifications",
      label: TITLES.notifications,
      icon: Bell,
      group: "Account",
      visible: true
    },
    {
      id: "agents",
      label: TITLES.agents,
      icon: Bot,
      group: "Account",
      visible: true
    },
    {
      id: "time-capture",
      label: TITLES["time-capture"],
      icon: Clock,
      group: "Account",
      visible: canCaptureTime
    },
    {
      id: "general",
      label: TITLES.general,
      icon: Building2,
      group: "Organization",
      visible: canManageGeneral
    },
    {
      id: "case-preparation",
      label: TITLES["case-preparation"],
      icon: ClipboardCheck,
      group: "Organization",
      visible: canSeeCasePreparation
    },
    {
      id: "team",
      label: TITLES.team,
      icon: Users,
      group: "Organization",
      visible: canManageTeam
    },
    {
      id: "time-billing",
      label: TITLES["time-billing"],
      icon: DollarSign,
      group: "Organization",
      visible: canManageTimeBilling
    },
    {
      id: "permissions",
      label: TITLES.permissions,
      icon: Lock,
      group: "Organization",
      visible: canManagePermissions
    },
    {
      id: "sso",
      label: TITLES.sso,
      icon: ShieldUser,
      group: "Organization",
      visible: canManageSso
    }
  ];
  const needle = query.trim().toLowerCase();
  const shown = nav.filter(
    (n) => n.visible && (!needle || n.label.toLowerCase().includes(needle))
  );

  const ssoSummary = ssoLoading
    ? "Loading…"
    : ssoState
    ? ssoState.ssoEnabled
      ? `Enabled · ${configuredProviderLabel || "Configured provider"}`
      : ssoState.tenantId
      ? `Not enabled · ${(ssoState.providers ?? []).length} provider${
          (ssoState.providers ?? []).length === 1 ? "" : "s"
        }`
      : "Off"
    : "Unable to load SSO settings";

  const padded = (node: React.ReactNode) => (
    <div className="px-6 pt-3 pb-8">{node}</div>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="flex h-165 max-h-[calc(100vh-2rem)] w-245 max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-245"
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <nav
          aria-label="Settings"
          className="flex h-full w-60 shrink-0 flex-col gap-3 overflow-y-auto border-r border-line-2 bg-chrome p-2"
        >
          <div className="-mb-1 flex h-8 shrink-0 items-center justify-between pr-1">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close"
              onClick={close}
            >
              <X />
            </Button>
            <Kbd>esc</Kbd>
          </div>
          <ListSearch
            value={query}
            onValueChange={setQuery}
            placeholder="Search settings"
            aria-label="Search Settings"
            className="w-full"
          />
          {(["Account", "Organization"] as const).map((group) => {
            const rows = shown.filter((n) => n.group === group);
            if (!rows.length) return null;
            return (
              <div key={group} className="flex flex-col gap-0.5">
                <span className="px-2 pb-1 text-sm text-fg-3">{group}</span>
                {rows.map((n) => (
                  <NavRow
                    key={n.id}
                    icon={n.icon}
                    active={visibleSection === n.id}
                    onClick={() => openSection(n.id)}
                  >
                    {n.label}
                  </NavRow>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface">
          {visibleSection !== "team" ? (
            <h2
              className={cn(
                "px-6 pt-7 text-xl font-medium text-fg",
                visibleSection === "profile" && "mx-auto w-full max-w-162"
              )}
            >
              {TITLES[visibleSection]}
            </h2>
          ) : null}

          {visibleSection === "profile" ? (
            <AccountTab onRequestClose={close} />
          ) : null}
          {visibleSection === "security" ? <SecurityTab /> : null}
          {visibleSection === "notifications" ? (
            <NotificationPreferences />
          ) : null}
          {visibleSection === "agents" ? (
            <AgentsTab
              lawFirmId={lawFirmId}
              canEditFirmInstructions={
                userType === "Lawyer" && isLawFirmAdminRole(role)
              }
              onOpenSection={canSeeCasePreparation ? openSection : undefined}
            />
          ) : null}
          {visibleSection === "time-capture"
            ? padded(<TimeCaptureSettings onRequestClose={close} />)
            : null}
          {visibleSection === "time-billing"
            ? padded(<TimeBillingSettings onRequestClose={close} />)
            : null}
          {visibleSection === "general" ? (
            <GeneralTab lawFirmId={lawFirmId} />
          ) : null}
          {visibleSection === "case-preparation" ? (
            <CasePreparationTab
              lawFirmId={lawFirmId}
              firmState={firmState}
              canEdit={canManageGeneral}
              onNavigate={close}
              focus={anchor}
            />
          ) : null}
          {visibleSection === "permissions" ? (
            <PermissionsTab lawFirmId={lawFirmId} />
          ) : null}
          {visibleSection === "sso"
            ? padded(
                <ListCard>
                  <ListRow
                    interactive
                    title="Single Sign-On (SSO)"
                    description={
                      <span className="text-sm text-fg-3">{ssoSummary}</span>
                    }
                    onClick={handleOpenSso}
                    control={
                      ssoState?.ssoEnabled ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSsoManagement();
                          }}
                        >
                          Manage
                          <ChevronRight />
                        </Button>
                      ) : (
                        <Switch
                          checked={Boolean(ssoState?.ssoEnabled)}
                          onCheckedChange={() => handleOpenSso()}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Single Sign-On (SSO)"
                        />
                      )
                    }
                  />
                </ListCard>
              )
            : null}
          {visibleSection === "team" ? (
            <TeamTab
              lawFirmId={lawFirmId}
              manageableLawFirms={manageableTeamFirms}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
